import {
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  VStack,
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Form, Formik } from 'formik'
import type { ReactElement } from 'react'
import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import useToast from '../../hooks/useToast'
import { UserContext } from '../../providers'

interface FormProps {
  billTo: '' | 'agency' | 'customer'
  imagekitId: string
  regionId: '' | 'aus' | 'eu1' | 'mum' | 'sgp' | 'us1' | 'us2'
  userEmail: string
  userName: string
}

interface Props {
  isOpen: boolean
  onClose(): void
}

const AddNewAgencyAccountModal = ({ isOpen, onClose }: Props): ReactElement => {
  const { t } = useTranslation()

  const toast = useToast()

  const { user } = useContext(UserContext)

  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const FormSchema = Yup.object().shape({
    billTo: Yup.string().required('Required'),
    imagekitId: Yup.string()
      .required('Required')
      .matches(/^[0-9a-zA-Z]*$/gu, 'Should be alphanumeric'),
    regionId: Yup.string().required('Required'),
    userEmail: Yup.string().required('Required'),
    userName: Yup.string().required('Required'),
  })

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          initialValues={{
            billTo: '',
            imagekitId: '',
            regionId: '',
            userEmail: '',
            userName: '',
          }}
          validationSchema={FormSchema}
          onSubmit={async values => {
            if (user) {
              await request.post(
                `/api/clients/${user.clientNumber}/agency/child/`,
                values,
              )

              if (request.error) {
                toast({
                  description: t(
                    'something_went_wrong',
                    'Something went wrong!',
                  ),
                  status: 'error',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
                return
              }

              toast({
                description: t(
                  'successfully_added_account',
                  'Successfully added account',
                ),
                status: 'success',
                isClosable: true,
                duration: 5000,
                position: 'top-right',
                variant: 'outline',
              })

              onClose()
            }
          }}
        >
          {() => (
            <Form>
              <ModalHeader>{t('add_account', 'Add Account')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4}>
                  <Field name="userName">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        isInvalid={Boolean(
                          form.errors[field.name] && form.touched[field.name],
                        )}
                        isRequired
                      >
                        <FormLabel>{t('client_name', 'Client Name')}</FormLabel>
                        <FormHelperText>
                          {t(
                            'agency_add_account_client_name_description',
                            'Name of the client that will be associated with ImageKit.',
                          )}
                        </FormHelperText>
                        <Input {...field} />
                        <FormErrorMessage>
                          {form.errors[field.name]}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="userEmail">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        isInvalid={Boolean(
                          form.errors[field.name] && form.touched[field.name],
                        )}
                        isRequired
                      >
                        <FormLabel>
                          {t('default_user_email', 'Default User Email')}
                        </FormLabel>
                        <FormHelperText>
                          {t(
                            'agency_add_account_user_email_description',
                            'Default email id to be associated with account.',
                          )}
                        </FormHelperText>
                        <Input {...field} />
                        <FormErrorMessage>
                          {form.errors[field.name]}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="imagekitId">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        isInvalid={Boolean(
                          form.errors[field.name] && form.touched[field.name],
                        )}
                        isRequired
                      >
                        <FormLabel>{t('imagekit_id', 'ImageKit Id')}</FormLabel>
                        <FormHelperText>
                          {t(
                            'agency_add_account_imagekit_id_description',
                            'Unique identifier that will be used by your client.',
                          )}
                        </FormHelperText>
                        <Input {...field} />
                        <FormErrorMessage>
                          {form.errors[field.name]}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="billTo">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        isInvalid={Boolean(
                          form.errors[field.name] && form.touched[field.name],
                        )}
                        isRequired
                      >
                        <FormLabel>{t('bill_to', 'Bill To')}</FormLabel>
                        <FormHelperText>
                          {t(
                            'agency_add_account_bill_to_description',
                            'Entity managing the billing process.',
                          )}
                        </FormHelperText>
                        <Select {...field}>
                          <option>Select</option>
                          <option value="agency">Agency</option>
                          <option value="customer">Customer</option>
                        </Select>
                        <FormErrorMessage>
                          {form.errors[field.name]}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="regionId">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        isInvalid={Boolean(
                          form.errors[field.name] && form.touched[field.name],
                        )}
                        isRequired
                      >
                        <FormLabel>
                          {t('processing_region', 'Processing Region')}
                        </FormLabel>
                        <FormHelperText>
                          {t(
                            'agency_add_account_processing_region_description',
                            'Region in which your client expects images to be served from.',
                          )}
                        </FormHelperText>
                        <Select {...field}>
                          <option>Select</option>
                          <option value="aus">Sydney (Australia)</option>
                          <option value="eu1">Frankfurt (Europe)</option>
                          <option value="mum">Mumbai (India)</option>
                          <option value="sgp">Singapore (Singapore)</option>
                          <option value="us1">
                            North Virginia (United States)
                          </option>
                          <option value="us2">
                            North California (United States)
                          </option>
                        </Select>
                        <FormErrorMessage>
                          {form.errors[field.name]}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </VStack>
              </ModalBody>

              <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                <Button onClick={onClose} variant="outline">
                  {t('cancel')}
                </Button>
                <Button type="submit">{t('submit')}</Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default AddNewAgencyAccountModal
